(() => {
  if (customElements.get('hifi-image-rotation')) return;

  const integer = (value, fallback, minimum, maximum) => {
    const parsed = /^\d+$/.test(String(value)) ? Number(value) : fallback;
    return Math.min(maximum, Math.max(minimum, parsed));
  };

  class HiFiImageRotation extends HTMLElement {
    connectedCallback() {
      const generation = (this.generation || 0) + 1;
      this.generation = generation;
      queueMicrotask(() => {
        if (this.isConnected && this.generation === generation) this.initialize();
      });
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.cleanup();
    }

    initialize() {
      this.cleanup();
      this.stage = this.querySelector('[data-rotation-stage]');
      this.atlas = this.querySelector('[data-rotation-atlas]');
      this.controls = this.querySelector('[data-rotation-controls]');
      this.range = this.querySelector('[data-rotation-range]');
      this.previous = this.querySelector('[data-rotation-previous]');
      this.next = this.querySelector('[data-rotation-next]');
      if (!this.stage || !this.atlas || !this.controls || !this.range || !this.previous || !this.next) return;
      this.columns = integer(this.dataset.columns, 6, 1, 24);
      this.rows = integer(this.dataset.rows, 4, 1, 24);
      this.count = integer(this.dataset.frameCount, 24, 1, this.columns * this.rows);
      this.frame = integer(this.dataset.start, 12, 0, this.count - 1);
      this.loop = this.dataset.loop === 'true';
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.previous.addEventListener('click', () => this.setFrame(this.frame - 1), options);
      this.next.addEventListener('click', () => this.setFrame(this.frame + 1), options);
      this.range.addEventListener('input', () => this.setFrame(Number(this.range.value)), options);
      this.stage.addEventListener('keydown', event => this.keyDown(event), options);
      this.stage.addEventListener('pointerdown', event => this.pointerDown(event), options);
      this.stage.addEventListener('pointermove', event => this.pointerMove(event), { ...options, passive: false });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.stage.addEventListener(type, () => this.endDrag(), options);
      }
      this.stage.addEventListener('pointerleave', () => {
        if (this.drag && !this.drag.active) this.endDrag();
      }, options);
      this.range.min = '0';
      this.range.max = String(this.count - 1);
      this.setFrame(this.frame);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.loadAtlas();
          }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      } else {
        this.loadAtlas();
      }
    }

    cleanup() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.image) {
        this.image.onload = null;
        this.image.onerror = null;
      }
      this.endDrag();
      this.ready = false;
      this.image = null;
      this.classList.remove('is-ready');
      if (this.controls) this.controls.hidden = true;
      if (this.atlas) this.atlas.hidden = true;
      if (this.stage) this.stage.tabIndex = -1;
    }

    loadAtlas() {
      if (this.image || !this.dataset.atlas) return;
      const image = new Image();
      this.image = image;
      image.decoding = 'async';
      image.onload = () => {
        if (!this.isConnected || this.image !== image) return;
        if (image.naturalWidth !== this.columns * 900 || image.naturalHeight !== this.rows * 700) return;
        this.atlas.style.backgroundImage = `url(${JSON.stringify(image.currentSrc || image.src)})`;
        this.atlas.style.backgroundSize = `${this.columns * 100}% ${this.rows * 100}%`;
        this.setFrame(this.frame);
        this.atlas.hidden = false;
        this.controls.hidden = this.count < 2;
        this.stage.tabIndex = this.count < 2 ? -1 : 0;
        this.ready = true;
        this.classList.add('is-ready');
      };
      image.onerror = () => {
        if (this.image === image) this.cleanup();
      };
      image.src = this.dataset.atlas;
    }

    setFrame(value) {
      if (!Number.isFinite(value) || !this.count) return;
      const requested = Math.round(value);
      this.frame = this.loop
        ? ((requested % this.count) + this.count) % this.count
        : Math.min(this.count - 1, Math.max(0, requested));
      const column = this.frame % this.columns;
      const row = Math.floor(this.frame / this.columns);
      const x = this.columns > 1 ? column / (this.columns - 1) * 100 : 0;
      const y = this.rows > 1 ? row / (this.rows - 1) * 100 : 0;
      this.atlas.style.backgroundPosition = `${x}% ${y}%`;
      this.range.value = String(this.frame);
      this.previous.disabled = !this.loop && this.frame === 0;
      this.next.disabled = !this.loop && this.frame === this.count - 1;
    }

    keyDown(event) {
      if (!this.ready || event.target !== this.stage) return;
      const values = { ArrowLeft: this.frame - 1, ArrowRight: this.frame + 1, Home: 0, End: this.count - 1 };
      if (!Object.hasOwn(values, event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      this.setFrame(values[event.key]);
    }

    pointerDown(event) {
      if (!this.ready || !event.isPrimary || event.button !== 0 || this.count < 2) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, frame: this.frame, active: false };
    }

    pointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          this.endDrag();
          return;
        }
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
        drag.active = true;
        this.stage.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      event.preventDefault();
      const step = Math.max(8, this.stage.getBoundingClientRect().width / Math.max(1, this.count - 1));
      this.setFrame(drag.frame + Math.round(dx / step));
    }

    endDrag() {
      const drag = this.drag;
      this.drag = null;
      this.classList.remove('is-dragging');
      if (drag && this.stage?.hasPointerCapture(drag.id)) this.stage.releasePointerCapture(drag.id);
    }
  }

  customElements.define('hifi-image-rotation', HiFiImageRotation);
})();
